import { useUnifiedLabels } from '../../hooks/useEmails'
import { cn } from '../atoms'
import { MailOpen } from 'lucide-react'

const CRITICIDAD_ORDER = ['CRITICO_MAXIMO', 'CRITICO', 'ALTO', 'MEDIO', 'BAJO', 'INFORMATIVO']

const CRITICIDAD_COLORS: Record<string, { dot: string; active: string; inactive: string }> = {
  CRITICO_MAXIMO: { dot: 'bg-red-600', active: 'border-b-red-600 text-red-600', inactive: 'text-gray-500 hover:text-red-600' },
  CRITICO: { dot: 'bg-red-500', active: 'border-b-red-500 text-red-500', inactive: 'text-gray-500 hover:text-red-500' },
  ALTO: { dot: 'bg-orange-500', active: 'border-b-orange-500 text-orange-500', inactive: 'text-gray-500 hover:text-orange-500' },
  MEDIO: { dot: 'bg-yellow-500', active: 'border-b-yellow-500 text-yellow-600', inactive: 'text-gray-500 hover:text-yellow-600' },
  BAJO: { dot: 'bg-green-500', active: 'border-b-green-500 text-green-600', inactive: 'text-gray-500 hover:text-green-600' },
  INFORMATIVO: { dot: 'bg-blue-500', active: 'border-b-blue-500 text-blue-600', inactive: 'text-gray-500 hover:text-blue-600' },
}

/** Valor especial para filtrar solo no leídos (no es un label de Gmail) */
export const UNREAD_TAB_VALUE = '__UNREAD__'

interface CriticidadTabsProps {
  selectedLabel: string | null
  onSelectLabel: (label: string | null) => void
  unreadCount?: number
}

export function CriticidadTabs({ selectedLabel, onSelectLabel, unreadCount }: CriticidadTabsProps) {
  const { data, isLoading } = useUnifiedLabels()

  const criticidadLabels = data?.grouped?.criticidad || []

  const labelsByCode: Record<string, { name: string; messageCount: number }> = {}
  for (const l of criticidadLabels) {
    const code = l.name.replace(/^CRITICIDAD\//, '')
    labelsByCode[code] = l
  }

  const totalEmails = criticidadLabels.reduce((sum, l) => sum + (l.messageCount || 0), 0)
  const isUnreadActive = selectedLabel === UNREAD_TAB_VALUE

  if (isLoading) {
    return (
      <div className="flex items-center gap-1 border-b border-gray-200 dark:border-gray-700 px-4">
        {CRITICIDAD_ORDER.map(code => (
          <div key={code} className="px-3 py-2.5 text-xs text-gray-400 animate-pulse">
            {code.replace('_', ' ')}
          </div>
        ))}
      </div>
    )
  }

  return (
    <div className="flex items-center gap-0 border-b border-gray-200 dark:border-gray-700 overflow-x-auto">
      {/* Tab "Todos" */}
      <button
        onClick={() => onSelectLabel(null)}
        className={cn(
          'flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap',
          !selectedLabel
            ? 'border-b-gray-900 dark:border-b-gray-100 text-gray-900 dark:text-gray-100'
            : 'border-b-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300',
        )}
      >
        <span>Todos</span>
        <span className={cn(
          'text-xs px-1.5 py-0.5 rounded-full',
          !selectedLabel
            ? 'bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900'
            : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400',
        )}>
          {totalEmails}
        </span>
      </button>

      {/* Tab "No leídos" */}
      <button
        onClick={() => onSelectLabel(isUnreadActive ? null : UNREAD_TAB_VALUE)}
        className={cn(
          'flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap',
          isUnreadActive
            ? 'border-b-indigo-500 text-indigo-600'
            : 'border-b-transparent text-gray-500 hover:text-indigo-600',
        )}
      >
        <MailOpen className="w-3.5 h-3.5" />
        <span>No leídos</span>
        {unreadCount != null && unreadCount > 0 && (
          <span className={cn(
            'text-xs px-1.5 py-0.5 rounded-full',
            isUnreadActive
              ? 'bg-indigo-500 text-white'
              : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400',
          )}>
            {unreadCount}
          </span>
        )}
      </button>

      {/* Tabs de criticidad */}
      {CRITICIDAD_ORDER.map(code => {
        const label = labelsByCode[code]
        const count = label?.messageCount || 0
        const colors = CRITICIDAD_COLORS[code]
        const isActive = selectedLabel === code || selectedLabel === `CRITICIDAD/${code}`

        return (
          <button
            key={code}
            onClick={() => onSelectLabel(isActive ? null : code)}
            className={cn(
              'flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap',
              isActive
                ? colors.active
                : colors.inactive,
            )}
          >
            <span className={cn('w-2 h-2 rounded-full', colors.dot)} />
            <span>{code.replace('_', ' ')}</span>
            {count > 0 && (
              <span className={cn(
                'text-xs px-1.5 py-0.5 rounded-full',
                isActive
                  ? 'bg-current text-white'
                  : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400',
              )}>
                {count}
              </span>
            )}
          </button>
        )
      })}
    </div>
  )
}
